/**
 * The "Aus pilk kodulehele" campaign family.
 *
 * Three letters that belong to one named outreach project rather than to a
 * shape: a first contact, the findings letter that follows it, and one nudge.
 * They exist because a cold letter that opens with a REASON for writing reads
 * differently from one that opens with an opinion about somebody's website. The
 * project name is that reason, and it is the same name on the phone and in the
 * report, so the recipient hears one thing twice instead of two things once.
 *
 * WHAT AN OPERATOR MUST DO BEFORE SENDING. The findings in these letters are
 * examples, not claims. Every line is written the way a real finding from the
 * development brain is written - one sentence, one measured fact - and every
 * line has to be replaced with that run's own findings before the letter goes
 * anywhere. A letter that tells a stranger four links are broken when nobody
 * measured it is worse than no letter: it is the exact thing the audit-first
 * method exists to not be. The card description in `messages/*.json` says so
 * where the operator picks the template.
 *
 * The name lives in one constant. Renaming the project is one edit here, not a
 * hunt through six strings, because a project name is the kind of thing that
 * gets decided twice.
 *
 * THE IDS ARE HANDLES, NOT LABELS. Once an operator has edited one of these
 * letters, the edit is stored against the preset id, so renaming an id orphans
 * their work and silently hands them the shipped wording back. Rename the
 * project in the constant and leave the ids where they are.
 */
import { b, design, footer, pick, PLAIN_PAGE, signature, type L, type Preset, type PresetLocale } from "./shared";

/** The project's name, unquoted. Used in headings, subjects and body copy. */
export const PROJECT_NAME: L = { et: "Aus pilk kodulehele", en: "An honest look at your website" };

/** The name in the quotation marks each language actually uses. */
const quoted = (locale: PresetLocale): string =>
  locale === "en" ? `"${PROJECT_NAME.en}"` : `„${PROJECT_NAME.et}”`;

// The example findings. Deliberately the shape the brain emits: one sentence,
// one fact, no adjectives. See the file comment - these get replaced.
const EXAMPLE_FINDINGS: L = {
  et:
    "Neli linki viivad tühjale lehele\n" +
    "Telefonis jääb osa avalehe pealkirjast ekraanist välja\n" +
    "Kolmel lehel puudub Google'i jaoks kirjeldus\n" +
    "Kontaktivorm liigub üle krüpteerimata ühenduse",
  en:
    "Four links lead to an empty page\n" +
    "On a phone, part of the home page heading runs off the screen\n" +
    "Three pages have no description for Google\n" +
    "The contact form travels over an unencrypted connection",
};

const INTRO_BODY = (locale: PresetLocale): string =>
  locale === "en"
    ? "<p>Hello,</p>" +
      `<p>As part of our ${quoted(locale)} project, we went over {{domain}}.</p>` +
      "<p>The project goes through the websites of Estonian companies and sends each one a short review of what needs fixing. The review is free and there is nothing to order. You may pass it on to whoever looks after your site.</p>" +
      "<p>We looked at what any visitor and any search engine sees. This is what stood out:</p>"
    : "<p>Tere,</p>" +
      `<p>Seoses projektiga ${quoted(locale)} käisime üle ka {{domain}}.</p>` +
      "<p>Projekti raames käime läbi Eesti ettevõtete kodulehed ja saadame igaühele lühikese ülevaate sellest, mis seal parandamist vajab. Ülevaade on tasuta ja midagi tellima ei pea. Võite selle anda edasi ükskõik kellele, kes teie lehte hooldab.</p>" +
      "<p>Vaatasime seda, mida näeb iga külastaja ja iga otsingumootor. Silma jäi järgmine:</p>";

const INTRO_CLOSE: L = {
  et:
    "<p>Iga rea taga on tõend: aadress, mõõdetud number ja ekraanipilt. Kui soovite terve nimekirja, vastake sellele kirjale. Saadan selle ühe lehena ja osta ei ole vaja midagi.</p>" +
    "<p>Kui teema praegu päevakorral ei ole, öelge julgelt. Rohkem ei tülita.</p>",
  en:
    "<p>Every line has evidence behind it: an address, a measured number and a screenshot. If you would like the whole list, just reply to this letter. I will send it as a single page and there is nothing to buy.</p>" +
    "<p>If this is not on your agenda right now, say so and I will not write again.</p>",
};

const REPORT_INTRO = (locale: PresetLocale): string =>
  locale === "en"
    ? `<p>As promised, here is the review of {{domain}}. It is part of the ${quoted(locale)} project and it costs you nothing.</p>` +
      "<p>Every line has a measured number behind it, and the address it came from.</p>"
    : `<p>Nagu lubatud, siin on {{domain}} ülevaade. See on osa projektist ${quoted(locale)} ja teile tasuta.</p>` +
      "<p>Iga rea taga on mõõdetud number ja aadress, kust see tuli.</p>";

const REPORT_EVIDENCE: L = {
  et: "Telefonis jääb avalehe pealkirjast 135 pikslit ekraanist välja.",
  en: "On a phone, 135 pixels of the home page heading run off the screen.",
};
const REPORT_EVIDENCE_BY: L = { et: "Mõõdetud teie avalehel", en: "Measured on your home page" };

const REPORT_MEANING: L = {
  et:
    "<p>Mida see äri jaoks tähendab: katkine link ja liiga väike nupp on külastaja, kes läks tagasi otsingusse. Lehe kirjelduse puudumine tähendab, et Google valib otsingutulemusse ise mingi juhusliku lause teie lehelt.</p>" +
    "<p>Ükski neist ei ole suur töö. Kõik on ühe päevaga ära tehtavad ja teie praegune tegija saab need ise korda teha.</p>",
  en:
    "<p>What this means for the business: a broken link and a too-small button are a visitor who went back to search. A missing page description means Google picks some random sentence from your page for the result instead.</p>" +
    "<p>None of these is a big job. All of them fit into a day, and whoever maintains your site now can fix them.</p>",
};

const REPORT_CLOSE: L = {
  et: "<p>Kui soovite, käime need 15 minutiga koos läbi. Kui ei soovi, on ka hästi - ülevaade jääb teile alles ja midagi tegema ei pea.</p>",
  en: "<p>If you like, we can walk through them together in 15 minutes. If not, that is fine too - the review stays with you and there is nothing you have to do.</p>",
};

const FOLLOWUP_BODY = (locale: PresetLocale): string =>
  locale === "en"
    ? "<p>Hello,</p>" +
      `<p>A while ago I sent a short review of the {{company}} website, as part of the ${quoted(locale)} project. If it got lost, I can send it again.</p>` +
      "<p>And if this is not running for you right now, reply with one word and I will stop writing.</p>"
    : "<p>Tere,</p>" +
      `<p>Saatsin mõni aeg tagasi {{company}} kodulehe kohta lühikese ülevaate, projekti ${quoted(locale)} raames. Kui see kaduma läks, saadan uuesti.</p>` +
      "<p>Ja kui see teema teie jaoks praegu ei jookse, vastake ühe sõnaga - rohkem ei kirjuta.</p>";

export const HONEST_LOOK_PRESETS: Preset[] = [
  {
    id: "honest-look-intro",
    key: "honestLookIntro",
    tone: "plain",
    subject: {
      et: `${PROJECT_NAME.et}: paar leidu {{domain}} pealt`,
      en: `${PROJECT_NAME.en}: a few findings on {{domain}}`,
    },
    build: (locale) =>
      design(
        [
          b("text", { html: INTRO_BODY(locale) }),
          b("list", { items: pick(EXAMPLE_FINDINGS, locale) }),
          b("text", { html: pick(INTRO_CLOSE, locale) }),
          signature(),
          b("divider"),
          footer(locale),
        ],
        PLAIN_PAGE,
      ),
  },
  {
    id: "honest-look-report",
    key: "honestLookReport",
    tone: "report",
    subject: {
      et: "{{company}} koduleht: mis vajab parandust",
      en: "The {{company}} website: what needs fixing",
    },
    build: (locale) =>
      design([
        b("heading", { text: `${pick(PROJECT_NAME, locale)}: {{domain}}`, level: "h2" }),
        b("text", { html: REPORT_INTRO(locale) }),
        b("list", { items: pick(EXAMPLE_FINDINGS, locale) }),
        b("quote", {
          text: pick(REPORT_EVIDENCE, locale),
          author: pick(REPORT_EVIDENCE_BY, locale),
        }),
        b("text", { html: pick(REPORT_MEANING, locale) }),
        b("button", {
          text: locale === "en" ? "Open the whole review" : "Vaata tervet ülevaadet",
          url: "https://husly.app",
          align: "left",
        }),
        b("text", { html: pick(REPORT_CLOSE, locale) }),
        signature(),
        b("divider"),
        footer(locale),
      ]),
  },
  {
    id: "honest-look-follow-up",
    key: "honestLookFollowUp",
    tone: "followup",
    subject: {
      et: `${PROJECT_NAME.et}: kas ülevaade jõudis kohale?`,
      en: `${PROJECT_NAME.en}: did the review reach you?`,
    },
    build: (locale) =>
      design(
        [
          b("text", { html: FOLLOWUP_BODY(locale) }),
          b("button", {
            text: locale === "en" ? "Send the review again" : "Saada ülevaade uuesti",
            url: "https://husly.app",
            align: "left",
          }),
          signature(),
          b("divider"),
          footer(locale),
        ],
        PLAIN_PAGE,
      ),
  },
];
